import React from 'react';
import { Shield, Eye, Lock, Database, Mail, Calendar, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center mb-4">
          <Shield className="h-12 w-12 text-green-600 mr-3" />
          <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
        </div>
        <div className="flex items-center justify-center text-sm text-gray-600">
          <Calendar className="h-4 w-4 mr-2" />
          <span>Last Updated: October 2025</span>
        </div>
        <p className="mt-4 text-lg text-gray-700 max-w-2xl mx-auto">
          This Privacy Policy describes how BBC Quick List ("we," "our," or "us") collects, uses, and protects your information when you use our platform and services.
        </p>
      </div>

      {/* Privacy Notice Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
        <div className="flex items-center mb-3">
          <Lock className="h-6 w-6 text-green-600 mr-3" />
          <h2 className="text-xl font-semibold text-green-900">Your Privacy Matters</h2>
        </div>
        <p className="text-green-800">
          We are committed to protecting your privacy and maintaining the security of your personal information. 
          We do not sell, rent, or share your personal data with third parties for marketing purposes.
        </p>
      </div>

      {/* Content Sections */}
      <div className="space-y-8">
        
        {/* Information We Collect */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Personal Information</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• Name and email address (required for account creation)</li>
                <li>• Phone number (optional, for security and communications)</li>
                <li>• Cryptocurrency wallet addresses (for payment processing)</li>
                <li>• Profile information you choose to provide</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Usage Information</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• Platform activity and feature usage</li>
                <li>• IP addresses and device information</li>
                <li>• Browser type and operating system</li>
                <li>• Pages visited and time spent on the platform</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Transaction Information</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• Payment and transaction history</li>
                <li>• Credit purchases and usage</li>
                <li>• PIF voucher activities</li>
                <li>• Matrix position and compensation data</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How We Use Your Information */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Eye className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• <strong>Platform Operations:</strong> To provide, maintain, and improve our services</li>
            <li>• <strong>Account Management:</strong> To create and manage your account, verify identity, and provide customer support</li>
            <li>• <strong>Transaction Processing:</strong> To facilitate payments, track commissions, and manage compensation plans</li>
            <li>• <strong>Communication:</strong> To send important updates, security alerts, and educational content</li>
            <li>• <strong>Security:</strong> To protect against fraud, unauthorized access, and other security threats</li>
            <li>• <strong>Analytics:</strong> To understand platform usage and improve user experience (anonymized data only)</li>
            <li>• <strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
          </ul>
        </section>

        {/* Information Sharing */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Information Sharing & Disclosure</h2>
          </div>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-900 mb-2">We Do NOT Share Your Information For:</h3>
              <ul className="text-red-800 space-y-1">
                <li>• Marketing or advertising purposes</li>
                <li>• Sale to data brokers or third-party marketers</li>
                <li>• Unrelated commercial purposes</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Limited Sharing When Necessary:</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• <strong>Payment Processors:</strong> Wallet addresses and transaction data for cryptocurrency payments (NOWPayments, etc.)</li>
                <li>• <strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li>• <strong>Security Purposes:</strong> To investigate fraud, security breaches, or violations of our Terms</li>
                <li>• <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of company assets</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Data Security */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Lock className="h-6 w-6 text-green-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Data Security</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• <strong>Encryption:</strong> All data transmissions are encrypted using industry-standard SSL/TLS protocols</li>
            <li>• <strong>Secure Storage:</strong> Personal information is stored on secure servers with access controls</li>
            <li>• <strong>Password Protection:</strong> User passwords are hashed and encrypted</li>
            <li>• <strong>Regular Backups:</strong> Data is regularly backed up to prevent loss</li>
            <li>• <strong>Access Controls:</strong> Only authorized personnel have access to user data</li>
            <li>• <strong>Monitoring:</strong> We continuously monitor for security threats and vulnerabilities</li>
          </ul>
        </section>

        {/* Cryptocurrency & Non-Custodial Payments */}
        <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-blue-900">Cryptocurrency & Non-Custodial Payments</h2>
          </div>
          <ul className="text-blue-800 leading-relaxed space-y-2">
            <li>• <strong>Non-Custodial:</strong> We do not store or control your cryptocurrency funds</li>
            <li>• <strong>Wallet Addresses:</strong> We store wallet addresses only to facilitate payment routing</li>
            <li>• <strong>Transaction Records:</strong> We maintain records of transactions for platform functionality and compliance</li>
            <li>• <strong>Third-Party Processors:</strong> Payment processing is handled by external services (NOWPayments)</li>
            <li>• <strong>Blockchain Transparency:</strong> Cryptocurrency transactions are recorded on public blockchains</li>
          </ul>
        </section>

        {/* Your Rights */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Your Privacy Rights</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Access & Control</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• View your personal information</li>
                <li>• Update your account details</li>
                <li>• Export your data</li>
                <li>• Delete your account</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Communication Preferences</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• Opt-out of marketing emails</li>
                <li>• Manage notification settings</li>
                <li>• Control communication frequency</li>
                <li>• Unsubscribe from newsletters</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Cookies & Tracking */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Eye className="h-6 w-6 text-orange-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Cookies & Tracking</h2>
          </div>
          <div className="space-y-3">
            <p className="text-gray-700">We use cookies and similar technologies to:</p>
            <ul className="text-gray-700 space-y-1">
              <li>• Keep you logged in to your account</li>
              <li>• Remember your preferences and settings</li>
              <li>• Analyze platform usage and performance</li>
              <li>• Protect against fraud and security threats</li>
            </ul>
            <p className="text-gray-700 text-sm">
              You can manage cookie preferences in your browser settings. Note that disabling certain cookies may affect platform functionality.
            </p>
          </div>
        </section>

        {/* Data Retention */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-gray-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Data Retention</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• <strong>Account Information:</strong> Retained while your account is active and for a reasonable period after closure</li>
            <li>• <strong>Transaction Records:</strong> Maintained for legal and tax compliance requirements (typically 7 years)</li>
            <li>• <strong>Usage Data:</strong> Anonymized analytics data may be retained indefinitely</li>
            <li>• <strong>Marketing Data:</strong> Deleted immediately upon unsubscribe or account deletion</li>
            <li>• <strong>Legal Holds:</strong> Data may be retained longer if required by legal proceedings</li>
          </ul>
        </section>

        {/* Children's Privacy */}
        <section className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-yellow-600 mr-3" />
            <h2 className="text-2xl font-bold text-yellow-900">Children's Privacy</h2>
          </div>
          <p className="text-yellow-800 leading-relaxed">
            BBC Quick List is not intended for use by individuals under 18 years of age. 
            We do not knowingly collect personal information from children. If we become aware that we have collected 
            personal information from a child, we will take steps to delete such information promptly.
          </p>
        </section>

        {/* Policy Updates */}
        <section className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <FileText className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Policy Updates</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, 
            legal requirements, or other factors. We will notify you of any material changes by posting the updated 
            policy on this page with a new "Last Updated" date. Continued use of the platform after such updates 
            constitutes acceptance of the revised policy.
          </p>
        </section>

        {/* Contact Information */}
        <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Mail className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-blue-900">Contact Us About Privacy</h2>
          </div>
          <div className="text-blue-800 leading-relaxed">
            <p className="mb-3">If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us:</p>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <p><strong>Email:</strong> <a href="mailto:privacy@bbcquicklist.com" className="hover:underline">privacy@bbcquicklist.com</a></p>
              <p><strong>General Support:</strong> <a href="mailto:support@bbcquicklist.com" className="hover:underline">support@bbcquicklist.com</a></p>
              <p className="text-sm mt-2">We will respond to privacy inquiries within 30 days.</p>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-gray-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-900">Privacy Protection</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Your privacy is important to us. We are committed to protecting your personal information and being transparent about our data practices.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/terms" className="text-blue-600 hover:text-blue-800 hover:underline">
              Terms & Conditions
            </a>
            <span className="text-gray-400">•</span>
            <a href="/faq" className="text-blue-600 hover:text-blue-800 hover:underline">
              Frequently Asked Questions
            </a>
            <span className="text-gray-400">•</span>
            <a href="mailto:privacy@bbcquicklist.com" className="text-blue-600 hover:text-blue-800 hover:underline">
              Privacy Questions
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;